import type { RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';

import type {
  EnumOrganizationMemberPermissions,
  EnumOrganizationPermissions,
} from '@/helpers/enums/permissions.enum';
import { EnumRoutes } from '@/helpers/enums/routes.enum';
import { pinia } from '@/pinia';
import { hiddenRoutes, initializeHiddenRoutes } from '@/router/hidden.router';
import { internalTeamRoutes } from '@/router/internal-team.router';
import { journeyRoutes } from '@/router/journey.router';
import { libraryRouter } from '@/router/library.router';
import { useAuthStore } from '@/stores/auth.store';
import { useOnboardingStore } from '@/stores/onboarding.store';
import { useOrganizationStore } from '@/stores/organzation.pinia';
import { usePermissionsStore } from '@/stores/permissoins.store';

import { authRoutes } from './auth.router';
import { checklistRoutes } from './checklist.router';
import { dashboardRoutes } from './dashboard.router';
import { financialRoutes } from './financials.router';
import { landingPageRoutes } from './landing.router';
import { externalCoursesRoutes } from './lms.externalCourses.route';
import { lmsRoutes } from './lms.router';
import { miscellaneousRoutes } from './miscellaneous.router';
import { profileRoutes } from './profile.router';
import { redirectAfterSuperToken } from './router-after-super-token.router';
import { socialRoutes } from './social.router';
declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    requiresAuth?: boolean;
    // AND semantics — user must hold every listed permission (matches the
    // nest @PermissionsCheckPolicies decorator).
    memberPermissions?: Array<EnumOrganizationMemberPermissions>;
    organizationPermissions?: Array<EnumOrganizationPermissions>;
    // OR semantics — user must hold at least one of the listed permissions.
    // Use these when the page should be visible to multiple roles, e.g. the
    // dashboard landing page accessible to anyone with course OR event access.
    memberPermissionsAny?: Array<EnumOrganizationMemberPermissions>;
    organizationPermissionsAny?: Array<EnumOrganizationPermissions>;
    needLanguage?: boolean;
    shouldHaveGroup?: boolean;
    groupMeta?: RouteMeta[];
    dontUseKey?: boolean;
    shouldAllowAnonymousCourseView?: boolean;
    shouldAllowAnonymousExternalCourseView?: boolean;
    shouldAllowAnonymousEventView?: boolean;
    shouldHaveCertificatesEnabled?: boolean;
    shouldHaveInternalTeamMode?: boolean;
    hiddenInInternalTeamMode?: boolean;
    // Org-scoped `onboarding-journey` config flag — mirrors
    // shouldHaveInternalTeamMode, enforced in permissoins.store.ts
    // checkOrganizationConfig.
    shouldHaveOnboardingJourney?: boolean;
    // Exempts a route from the onboarding-journey redirect guard below
    // (the wizard route itself, plus the internal-team hub that hosts the
    // resume CTA) — prevents redirect loops.
    allowDuringOnboarding?: boolean;
    isHiddenRoute?: boolean;
    activeRouteName?: string;
    showBetaLabel?: boolean;
    allowUnverified?: boolean;
    // Add other properties as needed...
  }
}
initializeHiddenRoutes();
const routes: Array<RouteRecordRaw> = [
  landingPageRoutes,
  authRoutes,
  lmsRoutes,
  financialRoutes,
  socialRoutes,
  profileRoutes,
  miscellaneousRoutes,
  dashboardRoutes,
  libraryRouter,
  checklistRoutes,
  // mobileRoutes,
  // chatRouter,
  externalCoursesRoutes,
  journeyRoutes,
  internalTeamRoutes,
].flat(3);
// remove all hidden routes from the routes array
const filteredRoutes: Array<RouteRecordRaw> = [];
routes.forEach((route) => {
  //check the route name if it is in the hidden routes array and if it has children then check the children names as well and remove the children if they are in the hidden routes array
  //check if has children
  if (route.children) {
    const children: Array<RouteRecordRaw> = [];
    //check children name if it is in the hidden routes array
    route.children.forEach((child) => {
      if (!hiddenRoutes.includes(child.name as string)) {
        children.push(child);
      }
    });
    route.children = children;
  }
  filteredRoutes.push(route);
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: filteredRoutes,
});

router.beforeEach(async (to, from, next) => {
  const orgStore = useOrganizationStore(pinia);
  const permissionsStore = usePermissionsStore(pinia);

  // Redirect unverified users to verify-email page (restricted session)
  // This must be the first check — an unverified user should not reach
  // course redirects, phone verification, assessments, or any other flow.
  // Exception: public routes (requiresAuth !== true) like about-us, privacy policy,
  // technical support, etc. are accessible to all users including unverified.
  const currentUser = useAuthStore(pinia).user;
  const isPublicRoute = !to.matched.some(
    (record) => record.meta.requiresAuth === true
  );
  if (currentUser && currentUser.isVerified === false) {
    if (
      !isPublicRoute &&
      !to.matched.some((record) => record.meta.allowUnverified) &&
      to.name !== EnumRoutes.auth.verifyEmail.name &&
      to.name !== EnumRoutes.auth.reportEmail.name
    ) {
      next({ name: EnumRoutes.auth.verifyEmail.name });
      return;
    }
    // Unverified user on an allowed page — skip all other checks
    // (assessments, phone verification, requiresLogout, etc.)
    next();
    return;
  }

  // Onboarding journey gate — "pulled up again" behavior (spec §1.4): an
  // authenticated, verified user with an incomplete journey is redirected
  // into the wizard from anywhere in the app. Only runs when the org has
  // the `onboarding-journey` config flag on, so orgs without the feature
  // pay no extra request. State is fetched at most once per session —
  // `fetchState` itself no-ops on repeat calls once `loaded` is true.
  //
  // Deliberately does NOT reuse the `currentUser` snapshot taken above:
  // on a cold reload this guard's beforeEach can run before App.vue's
  // onBeforeMount has awaited initializeAuth(), so `currentUser` may read
  // as null for an actually-logged-in visitor. initializeAuth() is
  // memoized (auth.store.ts), so awaiting it here either resolves
  // instantly (already-settled) or safely does the one real
  // initialization itself — never duplicated side effects — after which
  // `authStore.user` is authoritative. The verify-email block above has
  // the same underlying race and is left untouched — out of scope here.
  if (orgStore.onboardingJourneyEnabled) {
    const authStore = useAuthStore(pinia);
    await authStore.initializeAuth();
    if (authStore.user) {
      const onboardingStore = useOnboardingStore(pinia);
      if (!onboardingStore.loaded) {
        await onboardingStore.fetchState();
      }
      // NOTE: does NOT reuse `isPublicRoute` here — that exemption is only
      // valid for unauthenticated visitors (verify-email block above). An
      // authenticated user with an incomplete journey must still be
      // redirected off public routes too (e.g. the landing page), or a
      // reload at `/` would never pull them back in. Exempt only the
      // wizard itself/internal-team hub (allowDuringOnboarding) and the
      // auth/logout/verification routes the guard already classifies
      // elsewhere (requiresLogout for login/register; verify-email and
      // report-email by name, same as the block above) — avoids redirect
      // loops without opening the public-route hole.
      const isExemptRoute =
        to.matched.some((record) => record.meta.allowDuringOnboarding) ||
        to.matched.some((record) => record.meta.requiresLogout) ||
        to.name === EnumRoutes.auth.verifyEmail.name ||
        to.name === EnumRoutes.auth.reportEmail.name;
      if (
        !onboardingStore.error &&
        !onboardingStore.complete &&
        !isExemptRoute
      ) {
        next({ name: EnumRoutes.internalTeamRoutesEnum.onboarding.name });
        return;
      }
    }
  }

  // Watar / internal-team-mode orgs have no public landing page — visiting
  // the root ("/", which redirects into the landing route group) or any
  // marketing subpage (about, webinars, terms, etc.) sends guests straight
  // to login and authenticated visitors to the internal-team hub (their
  // home since the KP-68 nav consolidation). Runs after the onboarding gate
  // above, so an authenticated user mid-onboarding is still pulled into the
  // wizard first rather than bounced here.
  const isLandingRoute = to.matched.some((record) =>
    Object.values(EnumRoutes.landing).some(
      (route) => route.name === record.name
    )
  );
  if (orgStore.internalTeamMode && isLandingRoute) {
    // Same auth-ready race as the onboarding gate above: on a cold reload
    // this guard can run before App.vue's initializeAuth() has resolved.
    // initializeAuth() is memoized, so awaiting it here is always safe.
    const authStore = useAuthStore(pinia);
    await authStore.initializeAuth();
    if (authStore.user) {
      // KP-68: the internal-team home is the tabbed hub (/internal-team),
      // matching the post-login landing in auth.store handleSetLoginData —
      // the old My Courses landing is now the hub's "My Modules" tab. The
      // hub root carries allowDuringOnboarding, so this cannot loop with
      // the onboarding gate above.
      next({ name: EnumRoutes.internalTeamRoutesEnum.root.name });
    } else {
      next({ name: EnumRoutes.auth.login.name });
    }
    return;
  }

  if (to.name === EnumRoutes.lms.root.name) {
    // Internal-team orgs: the all-courses browsing surface is hidden
    // (hiddenInInternalTeamMode), so land directly on My Courses. The
    // existing branch below stays untouched for every other org (flag OFF
    // behaviour is byte-identical).
    if (orgStore.internalTeamMode) {
      if (
        permissionsStore.checkAuthentication(
          EnumRoutes.lms.myCourses.meta,
          false,
          EnumRoutes.lms.myCourses.name
        )
      )
        next({
          name: EnumRoutes.lms.myCourses.name,
        });
      else
        next({
          name: EnumRoutes.auth.login.name,
        });
      return;
    }
    if (
      permissionsStore.checkAuthentication(
        EnumRoutes.lms.root.meta,
        false,
        EnumRoutes.lms.root.name
      )
    )
      next({
        name: EnumRoutes.lms.allCourses.name,
      });
    else if (
      permissionsStore.checkAuthentication(
        EnumRoutes.lms.myCourses.meta,
        false,
        EnumRoutes.lms.myCourses.name
      )
    )
      next({
        name: EnumRoutes.lms.myCourses.name,
      });
    else
      next({
        name: EnumRoutes.auth.login.name,
      });
    return;
  }
  if (to.name === EnumRoutes.externalCourses.root.name) {
    if (
      permissionsStore.checkAuthentication(
        EnumRoutes.externalCourses.allExternalCourses.meta,
        false,
        EnumRoutes.externalCourses.allExternalCourses.name
      )
    )
      next({
        name: EnumRoutes.externalCourses.allExternalCourses.name,
      });
    else if (
      permissionsStore.checkAuthentication(
        EnumRoutes.externalCourses.myExternalCourses.meta,
        false,
        EnumRoutes.externalCourses.myExternalCourses.name
      )
    )
      next({
        name: EnumRoutes.externalCourses.myExternalCourses.name,
      });
    else
      next({
        name: EnumRoutes.auth.login.name,
      });
    return;
  }
  if (to.query?.super_token) {
    const onlyView = !!to.query?.student_view;
    if (onlyView) {
      localStorage.setItem('student_view', 'true');
      useAuthStore(pinia).instructorView = true;
    } else {
      sessionStorage.removeItem('instructorView');
      useAuthStore(pinia).instructorView = false;
    }
    await useAuthStore(pinia).loginSSO(`${to.query.super_token}`, onlyView);
    return next(redirectAfterSuperToken(to));
  }

  //handle ssoid token login
  if (to.query?.ssoid) {
    const isLoggedin = useAuthStore(pinia).user;
    // if the user is already logged in, then continue to the next route, but also send the ssoid token to the server
    if (isLoggedin) {
      await useAuthStore(pinia).handleSAMLSSO(`${to.query.ssoid}`);
    } else {
      //store the ssoid token in the local storage
      localStorage.setItem('ssoid', `${to.query.ssoid}`);

      // redirect to the login page and then login the user
      next({ name: EnumRoutes.auth.login.name });

      // ==> HELP still need to intercept the login process and send the ssoid token to the server after they login, and delete the ssoid token from the local storage
    }
    return;
  }

  // if the user is not logged in and the next route doesn't require authentication, then remove the previous route
  if (
    !to.matched.some(
      (record) =>
        record.name === EnumRoutes.auth.root.name ||
        record.name === EnumRoutes.auth.register.name
    )
  ) {
    const authStore = useAuthStore(pinia);
    authStore.setPreviousRoute(null);
  }
  // if we enter the site for the first time and there is no permissions in the locale storage we will fetch them
  if (
    localStorage.getItem('systemPermissions') === null ||
    localStorage.getItem('userPermissions') === null
  ) {
    await permissionsStore.initializePermissions();
  }
  useOrganizationStore(pinia).setTabTitle(to.meta.name as string | undefined);
  const meta = to.meta;
  permissionsStore.checkAuthentication(meta, true, to.fullPath);

  // if the route requires the user to be logged out, and the user is login, then redirect to the dashboard
  if (
    to.matched.some((record) => record.meta.requiresLogout) &&
    useAuthStore().user
  ) {
    next({ name: EnumRoutes.dashboard.root.name });
  } else next();
});
router.afterEach((to) => {
  const permissionsStore = usePermissionsStore(pinia);
  const authStore = useAuthStore(pinia);
  useOrganizationStore(pinia).setTabTitle(to.meta.name as string | undefined);
  const meta = to.meta;
  const auth = permissionsStore.checkAuthentication(meta, true, to.fullPath);
  if (auth) {
    authStore.shouldLogged = false;
    authStore.shouldHavePermission = false;
  }
});
export default router;
